import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register Chart.js modules
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

// Styled Components
const ChartWrapper = styled.div`
  position: relative; /* ✅ important */
  background-color: #f9f9f9;
  width: 100%;
  height: 55vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const CompanyAnalysis = ({ data }) => {
  const [salesData, setSalesData] = useState([]);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (data && data.length > 0) {
      // Group data by year and month, counting unique NazwaKontrahenta
      const groupedData = data.reduce((acc, row) => {
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        const companyName = row.NazwaKontrahenta;

        if (!acc[year]) acc[year] = Array.from({ length: 12 }, () => new Set());
        acc[year][month - 1].add(companyName);

        return acc;
      }, {});

      const years = Object.keys(groupedData).map((y) => parseInt(y, 10));
      setAvailableYears(years);

      const latestYear = Math.max(...years);
      setSelectedYear(latestYear);

      setSalesData(groupedData);
    }
  }, [data]);

  useEffect(() => {
    if (selectedYear && salesData[selectedYear]) {
      const monthlyData = salesData[selectedYear].map((set) => set.size);
const variation = monthlyData.map((value, index) => {
  if (index === 0) return 0;   // no previous month → 0%

  const prev = monthlyData[index - 1];

  if (prev === 0) {
    if (value === 0) return 0;     // 0 → 0  => 0%
    return 100;                    // 0 → >0 => +100% (just “big growth”)
  }

  return ((value - prev) / prev) * 100;
});


      // Colors: uniform green bars, green trend line
      const BAR_GREEN = "rgba(46, 125, 50, 0.7)";
      const BAR_GREEN_BORDER = "rgba(46, 125, 50, 1)";
      const LINE_GREEN = "rgba(46, 125, 50, 1)";
      const LINE_GREEN_FILL = "rgba(46, 125, 50, 0.12)";

      setChartData({
        labels: [
          "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
          "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
        ],
        datasets: [
          {
            type: "bar",
            label: "Liczba Współpracujących Firm",
            data: monthlyData,
            backgroundColor: BAR_GREEN,          // ✅ uniform green
            borderColor: BAR_GREEN_BORDER,
            borderWidth: 1,
          },
          {
            type: "line",
            label: "Trend Wzrostu/Spadku (%)",
            data: variation,
            borderColor: LINE_GREEN,             // ✅ green line
            backgroundColor: LINE_GREEN_FILL,
            fill: true,
            tension: 0.4,
            yAxisID: "percentage",
            pointRadius: 3,
          },
        ],
      });
    }
  }, [selectedYear, salesData]);

  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!salesData || availableYears.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="company-analysis-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="company-analysis-tooltip"
        place="left"
        content={
          <>
            <strong>Analiza Firm</strong>
            <br />
            Wykres pokazuje liczbę unikalnych firm współpracujących<br />
            w każdym miesiącu oraz procentową zmianę m/m.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: "Miesiąc" } },
              y: { title: { display: true, text: "Liczba Firm" }, beginAtZero: true },
              percentage: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Trend Wzrostu/Spadku (%)" },
                grid: { drawOnChartArea: false },
              },
            },
            plugins: {
              title: { display: true, text: `Ilość klientów ${selectedYear}` },
              tooltip: {
                callbacks: {
                  label: (context) =>
                    context.dataset.type === "line"
                      ? `Trend: ${Number(context.raw).toFixed(1)}%`
                      : `Firmy: ${context.raw}`,
                },
              },
            },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default CompanyAnalysis;
